// Read direction of the program editor: stored Program -> editor Draft.
// Split out of programEdit.ts for the 500-line cap; the write direction
// (Draft -> payload) lives in programEditPayload.ts.

import { blankSetRow } from "./programEditSeedRow.js";
import type {
  CellDraft,
  Draft,
  IntensityKind,
  SetRowDraft,
} from "./programEdit.js";
import { intensityToCell } from "./programEditBuild.js";
import {
  authoredLocaleOf,
  authoredTextIn,
  pickAuthoredText,
  type AuthoredText as AuthoredTextT,
} from "./authoredText.js";
import { coerceLocale, type Locale } from "./locale.js";
import type {
  NewProgram as NewProgramT,
  Program as ProgramT,
} from "./schemas/program.js";
import { NEW_PROGRAM_STATUS } from "./schemas/program.js";
import type { UnitsPreference } from "./schemas/profile.js";
import { uuidv4 } from "./uuid.js";

// Convert a stored Program into the matrix-shaped editor draft.
// Single-variant only on read: variants[0] gets the picker; extras are
// preserved in the wire shape but not exposed yet (5.2.3).
//
// `cellUnit` is the unit the absolute-weight cells come out in. Weights are
// stored in kg (units.ts), so an lb user has to READ lb in the cell or the
// number under the LB header would still be kilos. Whatever is passed here
// MUST be passed to buildPayload on the way back, or the round-trip scales
// every prescription by 2.2; useProgramEditor captures it once, at seed.
// ⛔ Required, not `= "kg"` (CN-26 wave 3): an optional unit here is exactly
// the defect the wave exists to remove: a caller that works in lb and
// forgets to pass it would silently read 225 lb as 225 kg.
export const programToDraft = (
  program: ProgramT,
  cellUnit: UnitsPreference,
  // PE-21: reader's language, used only to pick the editing language of
  // a program that never recorded an author. An existing program's own
  // language always wins.
  readerLocale?: Locale,
): Draft => {
  // The language the editor WRITES. `null` means we genuinely don't know:
  // the program carries naked strings and never recorded an author, which
  // is every program written before PE-21.
  //
  // ⛔ It must NOT fall back to the reader's language. That would let a
  // French reader open an Italian program, see "Français" as the active
  // language, and, the moment they corrected it, file the Italian text
  // under `fr` for ever. We don't know, so we say we don't know: with
  // `null` the draft behaves exactly as it did before PE-21 (naked
  // strings in, naked strings out) and the language chip stays out of the
  // way until somebody declares the language.
  //
  // ⭐ L'eccezione e' il programma NUOVO: un nome ancora vuoto non ha una
  // lingua da marcare male, e cio' che il coach sta per scrivere e' per
  // definizione nella sua. E' l'unico momento in cui la lingua d'autore
  // si conosce con certezza, quindi e' li' che si registra.
  const isBlank =
    program.name === undefined ||
    (typeof program.name === "string" && program.name.trim().length === 0);
  //
  // ⭐ PE-32: e il lettore viene PRIMA dell'autore quando il programma ha
  // davvero testo nella sua lingua. Suo smoke del 29/08: «io ho l'UI in
  // inglese, ma l'editor mi mostra l'italiano». `authoredLocaleOf` preferisce
  // per costruzione la lingua d'autore, quindi il lettore non era mai
  // consultato e non c'era modo di scoprirlo.
  // ⛔ Il predicato si ancora all'OGGETTO per-lingua, non a `authoredTextIn`
  // da solo: su una stringa nuda senza `authorLocale` quella risponde «si',
  // ho testo» per QUALUNQUE lingua, e un programma italiano mai dichiarato si
  // aprirebbe marcato inglese: il Nodo 3 di PE-21 §6, cioè l'italiano
  // archiviato sotto la chiave sbagliata al primo salvataggio.
  // ⛔ E una traduzione a MACCHINA conta come testo, esattamente come una
  // scritta a mano. Sua decisione del 29/08 notte, dopo lo smoke di
  // `4.37.0-dev`: *«il default deve essere la lingua di chi edita, come tutto
  // il resto»*. La condizione contraria era stata aggiunta qui il giorno
  // prima su una premessa FALSA: «oggi nessuno scrive `autoTranslated`»,
  // mentre il backfill del 22/08 l'ha scritto su tutti e 15 i programmi di
  // prod (`tooling/migrations/2026-08-22-pe21-authored-text-backfill`,
  // `_common.py:126`). Risultato: i Tetras, tradotti da `it`, si aprivano in
  // italiano a un lettore inglese, ed e' il difetto che lui ha smokato.
  // ⭐ Cio' che rende sicuro non escluderle: correggere una traduzione le
  // TOGLIE il marchio (`setAuthoredLocale`, authoredText.ts:160), quindi il
  // testo corretto diventa suo; e la lingua attiva ora e' dichiarata a
  // schermo dal chip di `PE-32`, quindi non e' piu' invisibile.
  // ⛔ E «lettore ignoto» non e' «lettore inglese». `coerceLocale(undefined)`
  // risponde `en`, che per il ramo `isBlank` andava bene (un programma nuovo
  // va scritto in QUALCHE lingua) ma qui direbbe un'altra cosa: che l'inglese
  // batte la lingua d'autore per un chiamante che non ha mai dichiarato un
  // lettore. Chi non lo passa non esprime una preferenza, quindi non ne vince
  // nessuna. (Review consolidamento: il chiamante senza lettore era
  // `apps/web/src/programs/ProgramEditor.tsx:40`, uscito con RW-14.)
  const readerL = coerceLocale(readerLocale);
  const readerHasText =
    readerLocale !== undefined &&
    program.name != null &&
    typeof program.name !== "string" &&
    authoredTextIn(program.name, readerL) !== "";
  const editLocale =
    (readerHasText ? readerL : null) ??
    authoredLocaleOf(program.name, program.authorLocale) ??
    (isBlank ? readerL : null);
  // A blank program is being authored right now, so the language it is
  // being written in IS its author language. An existing one keeps
  // whatever it declared, including nothing.
  const declaredAuthorLocale =
    program.authorLocale ?? (isBlank ? (editLocale ?? undefined) : undefined);
  // #77: testo per-lingua letto esatto nella lingua d'edit, come changeEditLocale.
  const text = (v: AuthoredTextT | string | undefined): string =>
    editLocale && v != null && typeof v !== "string"
      ? authoredTextIn(v, editLocale)
      : (pickAuthoredText(v, editLocale ?? readerLocale, program.authorLocale) ??
        "");
  return {
  name: text(program.name),
  description: text(program.description),
  nameRaw: program.name,
  descriptionRaw: program.description,
  editLocale,
  ...(declaredAuthorLocale ? { declaredAuthorLocale } : {}),
  status: program.status,
  cycleWeeks: program.cycleWeeks,
  sessionsPerWeek: program.sessionsPerWeek,
  ...(program.roundTypes ? { roundTypes: [...program.roundTypes] } : {}),
  ...(program.focus ? { focus: program.focus } : {}),
  ...(program.origin ? { origin: program.origin } : {}),
  // Stored programs always carry an assignedTo (min(1) on the schema).
  // On create-from-template the synth path passes `[""]`; treat that
  // as empty so we don't smuggle a sentinel string into the payload.
  assignedTo: program.assignedTo.filter((s) => s !== ""),
  // Chip input always starts empty; adds layer on top of the
  // preserved assignedTo via the resolveAssignees path on the server.
  assigneeEmails: [],
  sessions: program.sessions.map((s) => ({
    id: s.id,
    name: text(s.name),
    notes: text(s.notes),
    nameRaw: s.name,
    notesRaw: s.notes,
    slots: s.slots.map((slot) => {
      const v0 = slot.variants[0];
      // Inactive weeks (sets: []) carry "this slot doesn't run in week
      // w". The matrix shape is read from the first ACTIVE week.
      const inactiveWeeks = Array.from(
        { length: program.cycleWeeks },
        (_, w) => (slot.weeks[w]?.sets.length ?? 0) === 0,
      );
      // Pivot weeks[]→ rows[]: walk every week, collect each set
      // position into a row. #336 S1: as many rows as the longest round; a
      // shorter round marks the rows it lacks absent.
      const weeks = slot.weeks.slice(0, program.cycleWeeks);
      const setCount = Math.max(0, ...weeks.map((wk) => wk.sets.length));
      const rows: SetRowDraft[] = [];
      // Fold only if every round's last set can carry amrap (a countdown bout
      // cannot: buildWorkForCell.amrapAllowed). Otherwise the slot keeps the
      // flag and the payload re-emits it, so it survives the first save.
      const foldLegacyAmrap =
        slot.amrapLastSet === true &&
        weeks.every((wk) => wk.sets[wk.sets.length - 1]?.work?.kind !== "countdown");
      for (let r = 0; r < setCount; r++) {
        // The kind of the row is taken from the first week that has set r
        // (the typical case). A week whose set has another kind keeps it on
        // its cell (#336), so the save writes back what was read.
        const w0Set = weeks.find((wk) => wk.sets[r] !== undefined)?.sets[r];
        const kind: IntensityKind = w0Set?.intensity.kind ?? "none";
        // Same first-active-week convention as the intensity kind above:
        // the row carries one shape, the weeks carry the numbers.
        const workKind = w0Set?.work?.kind;
        const cells: CellDraft[] = [];
        const notesPerCell: string[] = [];
        const notesRawPerCell: (AuthoredTextT | undefined)[] = [];
        for (let w = 0; w < program.cycleWeeks; w++) {
          const cell = slot.weeks[w]?.sets[r];
          let reps = "0";
          let value = "";
          let cellKind: IntensityKind | undefined;
          if (cell) {
            reps = String(cell.reps);
            // The inverse of buildIntensity, shared with the PE-22 K1
            // adapter so the two directions cannot drift apart.
            const read = intensityToCell(cell.intensity, cellUnit);
            value = read.intensityValue;
            if (read.kind !== kind) cellKind = read.kind;
          }
          const draftCell: CellDraft = { reps, intensityValue: value };
          if (cellKind) draftCell.kind = cellKind;
          if (!cell && !inactiveWeeks[w]) draftCell.absent = true;
          // Legacy slot flag: the last set of each round is AMRAP (mirror of
          // buildPlannedSets). Normalised here once, so the editor only ever
          // sees the per-set flag (and its stop-rule row).
          const legacyLast =
            foldLegacyAmrap &&
            cell !== undefined &&
            r === (slot.weeks[w]?.sets.length ?? 0) - 1;
          if (cell?.amrap || legacyLast) draftCell.amrap = true;
          if (cell?.amrap && cell.amrapStop) draftCell.amrapStop = { ...cell.amrapStop };
          if (cell?.repsMin !== undefined) draftCell.repsMin = String(cell.repsMin);
          if (cell?.repsMax !== undefined) draftCell.repsMax = String(cell.repsMax);
          if (cell?.work?.kind === "countdown")
            draftCell.workDurationSec = String(cell.work.durationSec);
          if (cell?.restSec !== undefined)
            draftCell.restSec = String(cell.restSec);
          // HI-8: read back, or the first save would drop it.
          if (cell?.pause) {
            draftCell.pausePhase = cell.pause.phase;
            draftCell.pauseSec = String(cell.pause.sec);
          }
          cells.push(draftCell);
          notesPerCell.push(text(cell?.notes));
          notesRawPerCell.push(cell?.notes);
        }
        const row: SetRowDraft = {
          intensityKind: kind,
          cells,
          notesPerCell,
          notesRawPerCell,
        };
        if (workKind) row.workKind = workKind;
        rows.push(row);
      }
      // Empty slot (no sets recorded yet): surface a single blank row
      // so the user can start typing.
      if (rows.length === 0) rows.push(blankSetRow(program.cycleWeeks));
      return {
        id: slot.id,
        exerciseId: v0?.exerciseId ?? "",
        exerciseName: v0?.exerciseName,
        setRows: rows,
        instructions: text(slot.instructions),
        instructionsRaw: slot.instructions,
        // Folded into the last set of each round above, unless it could not be.
        amrapLastSet: (slot.amrapLastSet ?? false) && !foldLegacyAmrap,
        maxAttempt: slot.maxAttempt ?? false,
        rpeEnabled: slot.rpeEnabled ?? false,
        rirEnabled: slot.rirEnabled ?? false,
        group: slot.group
          ? {
              id: slot.group.id,
              kind: slot.group.kind,
              ...(slot.group.cadence !== undefined
                ? { cadence: slot.group.cadence }
                : {}),
              ...(slot.group.name !== undefined
                ? { name: slot.group.name }
                : {}),
            }
          : null,
        variantsRaw: slot.variants,
        inactiveWeeks,
        ...(slot.rowId ? { rowId: slot.rowId } : {}),
        ...(slot.side ? { side: slot.side } : {}),
      };
    }),
  })),
  };
};

// Synthesise a Program-shaped value from a NewProgram creation draft so
// programToDraft can produce the editor's internal Draft shape with no
// branching on create-vs-edit. The synthesised id stays empty: the
// editor uses that as the POST-vs-PATCH discriminator.
export const newProgramToProgram = (n: NewProgramT): ProgramT => ({
  id: "",
  createdBy: "",
  assignedTo: n.assignedTo && n.assignedTo.length > 0 ? n.assignedTo : [""],
  // CO-17: carried verbatim from the creation payload, never reassigned
  // here: this synthesises the SEED program a brand-new draft edits, and
  // the seed's authoredAs is already the view active when the "+" was
  // pressed (buildPayload/draftToNewPlan stamp it, not this function).
  authoredAs: n.authoredAs,
  name: n.name,
  description: n.description,
  cycleWeeks: n.cycleWeeks,
  sessionsPerWeek: n.sessionsPerWeek,
  ...(n.roundTypes ? { roundTypes: n.roundTypes } : {}),
  ...(n.focus ? { focus: n.focus } : {}),
  ...(n.origin ? { origin: n.origin } : {}),
  status: n.status ?? NEW_PROGRAM_STATUS,
  createdAt: new Date(0).toISOString(),
  updatedAt: new Date(0).toISOString(),
  sessions: n.sessions.map((s) => ({
    id: s.id ?? "",
    name: s.name,
    notes: s.notes,
    slots: s.slots.map((slot) => ({
      ...slot,
      // A seed (e.g. a public model) carries no slot ids; the editor selects
      // and removes by id, so mint them here (the one seed -> draft seam).
      id: slot.id ?? uuidv4(),
    })),
  })),
});

/** A program from scratch (§5.4b «Da zero», the web's setup stage): `days`
 *  empty days D1..Dn over `rounds` rounds. The save asks for an exercise in
 *  each day. `authoredAs` is a placeholder: the save stamps the real one
 *  (CO-17). */
export const blankProgramSeed = (name: string, rounds: number, days: number): NewProgramT => ({
  authoredAs: "athlete",
  name,
  cycleWeeks: rounds,
  sessionsPerWeek: days,
  sessions: Array.from({ length: days }, (_, i) => ({ name: `D${i + 1}`, slots: [] })),
});
